/**
 * @fileoverview Authentication Hook
 * 
 * Provides comprehensive authentication functionality including sign up, sign in,
 * password management, magic links, and OTP verification. All authentication
 * events are logged to the security audit trail.
 * 
 * @module hooks/useAuth
 * @see {@link docs/ARCHITECTURE.md} for system overview
 * 
 * @example
 * ```tsx
 * const { signIn, signUp, signOut, loading } = useAuth();
 * 
 * // Sign in with email/password
 * await signIn('user@example.com', 'password');
 * 
 * // Sign up new user
 * await signUp('user@example.com', 'password', 'John Doe', '+1234567890');
 * ```
 * 
 * @security
 * - All authentication events are logged to security_audit_log
 * - Rate limiting is detected and handled gracefully
 * - Failed login attempts are tracked
 * - Unconfirmed emails automatically trigger resend
 */

import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

/**
 * Core authentication hook providing all auth operations.
 * 
 * @returns Authentication methods and loading state
 * 
 * @property signUp - Register a new user account
 * @property signIn - Sign in with email/password
 * @property signOut - Sign out and clear session
 * @property resetPassword - Send password reset email
 * @property updatePassword - Change current user's password
 * @property signInWithMagicLink - Passwordless email sign in
 * @property verifyOtp - Verify one-time password
 * @property resendOtp - Resend verification code
 * @property loading - Authentication operation in progress
 */
export function useAuth() {
  const [loading, setLoading] = useState(false);

  /**
   * Registers a new user account.
   * 
   * Creates account with email/password and optional metadata.
   * User must confirm email before accessing protected features.
   * 
   * @param email - User's email address
   * @param password - Password (min 6 characters)
   * @param fullName - User's display name
   * @param phone - Optional phone number
   * @returns Result with user data or error
   */
  const signUp = async (email: string, password: string, fullName: string, phone?: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName,
            phone: phone || null,
          },
        },
      });

      if (error) throw error;

      // Don't show success toast here - let the verification screen handle it
      return { data, error: null };
    } catch (error: any) {
      const message = error.message || "Failed to create account";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Signs in user with email and password.
   * 
   * Handles special cases:
   * - Unconfirmed email: auto-resends confirmation
   * - Rate limiting: shows appropriate message
   * - Failed attempts: logged to security audit
   * 
   * @param email - User's email address
   * @param password - User's password
   * @returns Result with session data or error
   */
  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Log failed login attempt
        if (data?.user?.id) {
          await supabase.from("security_audit_log").insert({
            user_id: data.user.id,
            event_type: "failed_login",
            ip_address: null,
            user_agent: navigator.userAgent,
            metadata: { error: error.message },
          });
        }

        // Check for email not confirmed error specifically
        if (error.message?.includes("Email not confirmed") || error.status === 400) {
          // Auto-resend confirmation email
          const handleResend = async () => {
            await supabase.auth.resend({
              type: 'signup',
              email: email,
              options: {
                emailRedirectTo: `${window.location.origin}/`,
              },
            });
            toast({
              title: "Confirmation Email Resent",
              description: "Check your inbox for a new confirmation link.",
            });
          };

          toast({
            title: "Email Not Confirmed",
            description: "Please check your email and click the confirmation link to verify your account.",
            variant: "destructive",
          });

          // Automatically resend after a brief delay
          setTimeout(handleResend, 2000);
          
          return { data: null, error };
        }
        
        throw error;
      }

      // Log successful login
      if (data?.user) {
        await supabase.from("security_audit_log").insert({
          user_id: data.user.id,
          event_type: "login",
          ip_address: null,
          user_agent: navigator.userAgent,
          metadata: { method: "password" },
        });
      }

      toast({
        title: "Welcome back!",
        description: "You've successfully signed in.",
      });

      return { data, error: null };
    } catch (error: any) {
      // Check for rate limiting
      if (error.status === 429 || error.message?.includes("rate limit")) {
        toast({
          title: "Too Many Attempts",
          description: "Please wait a moment before trying again.",
          variant: "destructive",
        });
      } else {
        const message = error.message || "Failed to sign in";
        toast({
          title: "Error",
          description: message,
          variant: "destructive",
        });
      }
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Signs out the current user and clears session data.
   * 
   * Clears local/session storage (preserving theme preferences)
   * and redirects to auth page with signed_out indicator.
   */
  const signOut = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Clear local storage (keep theme and platform preferences)
      const itemsToKeep = ['theme', 'platform_settings'];
      const allKeys = Object.keys(localStorage);
      allKeys.forEach(key => {
        if (!itemsToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear session storage
      sessionStorage.clear();
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Log logout
      if (user) {
        await supabase.from("security_audit_log").insert({
          user_id: user.id,
          event_type: "logout",
          ip_address: null,
          user_agent: navigator.userAgent,
          metadata: {},
        });
      }

      toast({
        title: "Signed out",
        description: "Come back soon!",
      });

      // Redirect to auth page with signed_out indicator
      window.location.href = '/auth?signed_out=true';
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to sign out",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sends password reset email to user.
   * 
   * @param email - Email address to send reset link to
   * @returns Result with error if failed
   */
  const resetPassword = async (email: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: "Reset link sent",
        description: "Check your email for the password reset link.",
      });

      return { error: null };
    } catch (error: any) {
      const message = error.message || "Failed to send reset link";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      return { error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates current user's password.
   * 
   * @param newPassword - New password (min 6 characters)
   * @returns Result with success flag and optional error
   */
  const updatePassword = async (newPassword: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: "Password updated",
        description: "Your password has been successfully changed. Logging you in...",
      });

      return { error: null, success: true };
    } catch (error: any) {
      const message = error.message || "Failed to update password";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      return { error, success: false };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sends magic link for passwordless sign in.
   * 
   * @param email - Email address to send link to
   * @returns Result with data or error
   */
  const signInWithMagicLink = async (email: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;

      toast({
        title: "Magic Link Sent",
        description: "Check your email for a sign-in link. It will expire in 60 minutes.",
      });

      return { data, error: null };
    } catch (error: any) {
      const message = error.message || "Failed to send magic link";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verifies one-time password from email.
   * 
   * @param email - User's email address
   * @param token - OTP code from email
   * @param type - Verification type: 'signup' | 'magiclink' | 'email'
   * @returns Result with session data or error
   */
  const verifyOtp = async (email: string, token: string, type: "signup" | "magiclink" | "email") => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: type === "magiclink" ? "email" : type,
      });

      if (error) throw error;

      // Log successful OTP verification
      if (data?.user) {
        await supabase.from("security_audit_log").insert({
          user_id: data.user.id,
          event_type: "otp_verified",
          ip_address: null,
          user_agent: navigator.userAgent,
          metadata: { 
            method: "otp",
            type,
            verified_at: new Date().toISOString()
          },
        });
      }

      toast({
        title: "Verification Successful",
        description: "You're now signed in.",
      });

      return { data, error: null };
    } catch (error: any) {
      const message = error.message || "Failed to verify code";
      toast({
        title: "Verification Failed",
        description: message,
        variant: "destructive",
      });
      
      // Log failed verification
      await supabase.from("security_audit_log").insert({
        event_type: "login",
        user_id: null,
        ip_address: null,
        user_agent: navigator.userAgent,
        metadata: { 
          type: 'otp_failed',
          email,
          error: message,
          failed_at: new Date().toISOString()
        },
      });
      
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resends verification OTP to email.
   * 
   * @param email - User's email address
   * @param type - Resend type: 'signup' | 'email_change'
   * @returns Result with error if failed
   */
  const resendOtp = async (email: string, type: "signup" | "email_change" = "signup") => {
    setLoading(true);
    try {
      const resendType: "signup" | "email" = type === "email_change" ? "email" : "signup";
      const { error } = await supabase.auth.resend({
        type,
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;

      // Log resend attempt
      await supabase.from("security_audit_log").insert({
        event_type: "login",
        user_id: null,
        ip_address: null,
        user_agent: navigator.userAgent,
        metadata: {
          type: 'otp_resent',
          email,
          otp_type: type,
          resent_at: new Date().toISOString()
        },
      });

      toast({
        title: "Code resent",
        description: "A new verification code has been sent to your email.",
      });

      return { error: null };
    } catch (error: any) {
      const message = error.message || "Failed to resend code";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
      return { error };
    } finally {
      setLoading(false);
    }
  };

  return { 
    signUp, 
    signIn, 
    signOut, 
    resetPassword, 
    updatePassword, 
    signInWithMagicLink,
    verifyOtp,
    resendOtp,
    loading 
  };
}
